/**
 * Taps: raw watch sockets a hand opens with a ticket it minted (DESIGN §3.2
 * rows 29–30). The Tap dropped on a World ring mints a single-use ticket
 * (watchTicket); the socket opens with it at once, and every frame it hears
 * streams into the Wire as it came. A second Tap drop on the same ring, or
 * L on the focused ring, unplugs it. A tap watches without presence, so it
 * never shows a peer that is not there; the live links keep the app's own
 * watches, and a tap never feeds the reconciler.
 */

import type { Taps, TapEvent, Unsub, WorldKey } from '../contracts';
import { watchUrl } from './link';

export interface TapDeps {
  readonly apiBase: string;
  readonly socket?: (url: string) => WebSocket;
}

export const createTaps = (deps: TapDeps): Taps => {
  const make = deps.socket ?? ((url: string) => new WebSocket(url));
  const open = new Map<WorldKey, WebSocket>();
  const listeners = new Set<(world: WorldKey, e: TapEvent) => void>();
  const emit = (world: WorldKey, e: TapEvent): void => {
    for (const fn of [...listeners]) {
      try { fn(world, e); } catch { /* a listener's fault stays its own */ }
    }
  };

  const close = (world: WorldKey): boolean => {
    const ws = open.get(world);
    if (!ws) return false;
    open.delete(world);
    try { ws.close(1000, 'unplugged'); } catch { /* already gone */ }
    emit(world, { kind: 'closed', code: 1000, why: 'unplugged' });
    return true;
  };

  return {
    open(world, ticket) {
      close(world);
      let ws: WebSocket;
      try {
        ws = make(watchUrl(deps.apiBase, world, ticket, false));
      } catch (error) {
        emit(world, { kind: 'closed', code: 0, why: String(error) });
        return;
      }
      open.set(world, ws);
      ws.onopen = () => { if (open.get(world) === ws) emit(world, { kind: 'open' }); };
      ws.onmessage = (m: MessageEvent) => {
        if (open.get(world) === ws && typeof m.data === 'string' && m.data !== 'pong') emit(world, { kind: 'frame', text: m.data });
      };
      ws.onclose = (c: CloseEvent) => {
        // Unplugged by hand is told once, by `close`; the World closing it is told here.
        if (open.get(world) !== ws) return;
        open.delete(world);
        emit(world, { kind: 'closed', code: c.code ?? 1006, why: c.reason ?? '' });
      };
    },
    close,
    has: (world) => open.has(world),
    closeAll() {
      for (const world of [...open.keys()]) close(world);
    },
    on(fn): Unsub {
      listeners.add(fn);
      return () => { listeners.delete(fn); };
    },
  };
};
